import PropTypes from 'prop-types';
import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import styled from 'styled-components';
import { connect } from 'react-redux';
import { compose, lifecycle } from 'recompose';
import { createStructuredSelector } from 'reselect';
import ImmutablePropTypes from 'react-immutable-proptypes';
import CircularProgress from '@material-ui/core/CircularProgress';

import * as eventActions from '@modules/events/actions/event';
import * as eventFormActions from '@modules/events/actions/eventform';
import {
  eventDataSelector,
  eventTagsDataSelector,
  eventObjectsDataSelector,
} from '@modules/events/selectors/event';
import { eventFormListSelector } from '@modules/events/selectors/eventform';
import EventHeader from '@modules/events/pages/eventDetail/components/EventHeader';
import EventSummary from '@modules/events/pages/eventDetail/components/EventSummary';
import EventFileAttachmentsForm from '@modules/events/pages/eventDetail/components/EventFileAttachmentsForm';
import EventImageAttachmentsForm from '@modules/events/pages/eventDetail/components/EventImageAttachmentsForm';
import EventDetailForm from '@modules/events/pages/eventDetail/components/EventDetailForm';
import EventRevisionHistory from '@modules/events/pages/eventDetail/components/EventRevisionHistory';
import ImageAttachmentDetail from '@modules/events/pages/eventDetail/components/ImageAttachmentDetail';

import {
  getEventForm,
  formatEventValues,
  defaultEventValues,
} from '@modules/events/pages/eventDetail/components/EventDetailFormHelper';

const EventDetail = ({
  event,
  eventID,
  eventFormList,
  eventObjects,
  tags,
  formValues,
  ownerID,
  onClose,
  onCreateSuccess,
}) => {
  const [isOpenRH, setIsOpenRH] = useState(false);
  const [selectedAttachment, setSelectedAttachment] = useState(null);

  const formID = eventID ? event.formID : formValues.formID;
  const getInitialValues = () => {
    const curEventForm = getEventForm(eventFormList, formID);
    if (eventID) {
      return {
        ...formatEventValues(event, curEventForm?.formFields || []),
        tags: tags.map(t => t.tag),
        objectIDList: eventObjects.map(o => `${o.objectID}`),
      };
    }

    return {
      formID,
      description: curEventForm?.description,
      dataObject: curEventForm?.dataObject,
      formGroup: curEventForm?.formGroup,
      objectIDList: [`${ownerID}`],
      ...defaultEventValues(curEventForm?.formFields || [], { eventData: {} }),
    };
  };

  return (
    <EventDetail.Wrapper>
      <Helmet>
        <title>Event Record</title>
      </Helmet>
      {eventID && !event.eventID ? (
        <EventDetail.LoadingWrapper>
          <CircularProgress />
        </EventDetail.LoadingWrapper>
      ) : (
        <React.Fragment>
          <EventHeader
            eventID={eventID}
            onBackToSearch={onClose}
            isOpenRH={isOpenRH}
            setOpenRH={v => setIsOpenRH(v)}
          />
          {isOpenRH ? (
            <EventRevisionHistory eventID={eventID} />
          ) : selectedAttachment !== null ? (
            <ImageAttachmentDetail
              selectedAttachment={selectedAttachment}
              eventList={[]}
              eventID={eventID}
              onBack={() => setSelectedAttachment(null)}
            />
          ) : (
            <>
              {eventID && <EventSummary formID={formID} />}
              <EventDetailForm
                eventID={eventID}
                hideCreateButtons={true}
                initialValues={getInitialValues()}
                objectIDReadOnly={true}
                onCreateRedirect={onClose}
                onCreateSuccess={onCreateSuccess}
              />
              {eventID && <EventFileAttachmentsForm eventID={eventID} />}
              {eventID && (
                <EventImageAttachmentsForm
                  eventID={eventID}
                  setSelectedAttachment={setSelectedAttachment}
                />
              )}
            </>
          )}
        </React.Fragment>
      )}
    </EventDetail.Wrapper>
  );
};

EventDetail.propTypes = {
  event: PropTypes.object,
  eventID: PropTypes.string,
  eventFormList: ImmutablePropTypes.map,
  eventObjects: PropTypes.array,
  formValues: PropTypes.object.isRequired,
  ownerID: PropTypes.number,
  tags: PropTypes.array,
  onClose: PropTypes.func.isRequired,
  onCreateSuccess: PropTypes.func.isRequired,
};

EventDetail.Wrapper = styled.div`
  min-height: calc(100vh - 65px);
  background-color: white;
`;

EventDetail.LoadingWrapper = styled.div`
  min-height: calc(100vh - 120px);
  display: flex;
  align-items: center;
  justify-content: center;
`;

const mapStateToProps = createStructuredSelector({
  event: eventDataSelector,
  eventFormList: eventFormListSelector,
  eventObjects: eventObjectsDataSelector,
  formValues: state => {
    const valuesPath = ['form', 'eventDetailForm', 'values'];
    return state.hasIn(valuesPath) ? state.getIn(valuesPath).toJS() : {};
  },
  tags: eventTagsDataSelector,
});

const mapDispatchToProps = {
  fetchEvent: eventActions.fetchEvent,
  fetchEventObjects: eventActions.fetchEventObjects,
  fetchEventTags: eventActions.fetchEventTags,
  fetchEventFormsByType: eventFormActions.fetchEventFormsByType,
  resetEvent: eventActions.resetEvent,
};

const enhance = compose(
  connect(
    mapStateToProps,
    mapDispatchToProps,
  ),
  lifecycle({
    componentDidMount() {
      const { eventID } = this.props;

      // skip reloading for same event
      if (this.props.event.eventID === parseInt(eventID, 10)) {
        return;
      }

      this.props.resetEvent();
      this.props
        .fetchEventFormsByType({ payload: { type: 'owner' } })
        .then(() => {
          if (eventID) {
            this.props.fetchEvent({ eventID });
            this.props.fetchEventTags({ eventID });
            this.props.fetchEventObjects({ eventID });
          }
        });
    },
  }),
);

export default enhance(EventDetail);
